import ReactECharts from 'echarts-for-react';
import industryProblems from '../../data/industryProblems.json';
import { useIsDark } from '../../useIsDark';

/**
 * HallucinationRateChart — hallucination rate by frontier model (blog14, Fig 1).
 *
 * Chart-type-first: per-model rates → ECharts horizontal bar. Data reads from
 * industryProblems.json (figures.hallucination_by_model) — the single data file
 * for the post. Brand colors: teal core, amber ≥9%, red ≥15% (the heavy
 * reasoning models). Theme: `dark` prop override, otherwise follows the live
 * html class theme (RULES_BLOG_POST Rule 29). ONE shared formatter.
 */

const BRAND = {
  teal: '#0AB3BF',
  warning: '#F59E0B',
  error: '#EF4444',
};

type RateItem = { label: string; value: number };

const formatPct = (v: number) => `${v.toFixed(1)}%`;

export const HallucinationRateChart = ({
  height = 460,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const fig = (
    industryProblems as unknown as {
      figures: { hallucination_by_model: { source: string; items: RateItem[] } };
    }
  ).figures.hallucination_by_model;

  const C = isDark
    ? {
        bg: '#0F172A',
        axis: '#E2E8F0',
        muted: '#94A3B8',
        grid: '#334155',
        card: 'bg-slate-900',
        border: 'border-slate-800',
        caption: 'text-slate-400',
      }
    : {
        bg: '#FFFFFF',
        axis: '#334155',
        muted: '#64748B',
        grid: '#334155',
        card: 'bg-white',
        border: 'border-slate-200',
        caption: 'text-slate-500',
      };

  const items = [...fig.items].sort((a, b) => a.value - b.value);
  const colorFor = (v: number) => (v >= 15 ? BRAND.error : v >= 9 ? BRAND.warning : BRAND.teal);

  const option = {
    backgroundColor: C.bg,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { marker?: string; name: string; value: number }[]) =>
        params.map((p) => `${p.marker} ${p.name}: <b>${formatPct(p.value)}</b>`).join('<br/>'),
    },
    grid: { left: 124, right: 56, top: 16, bottom: 44 },
    xAxis: {
      type: 'value',
      name: 'hallucination rate (%)',
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.35 } },
    },
    yAxis: {
      type: 'category',
      data: items.map((i) => i.label),
      axisLine: { lineStyle: { color: C.grid } },
      axisLabel: { color: C.axis, fontSize: 12 },
      axisTick: { show: false },
    },
    series: [
      {
        name: 'Hallucination rate',
        type: 'bar',
        barWidth: '55%',
        data: items.map((i) => ({
          value: i.value,
          itemStyle: { color: colorFor(i.value), borderRadius: [0, 6, 6, 0] },
        })),
        label: {
          show: true,
          position: 'right',
          color: C.axis,
          fontWeight: 700,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption} text-center`}>
        Figure 1. Hallucination rate by frontier model on a trivial summarization task ({fig.source}).
        Even the best models invent content 3–12% of the time; the largest reasoning models are worse.
      </p>
    </div>
  );
};
